import React from 'react';
import { TextArea, Select, Input, Checkbox } from 'common/components';
import { SMSMode, Prompt } from '../../WorkflowBuilder.types';
import { useFormContext, Controller } from 'react-hook-form';
import { FormNodeConfigProps } from '../../NodeConfigModal';

import styles from '../../NodeConfigModal.module.scss';

interface NodeConfigSmsProps {
    isLoadingPrompts: boolean;
    prompts?: Prompt[];
}

export const NodeConfigSms: React.FC<NodeConfigSmsProps> = ({ isLoadingPrompts, prompts }) => {
    const {
        register,
        watch,
        control,
        setValue,
        formState: { errors },
    } = useFormContext<FormNodeConfigProps>();

    const smsMode = watch('smsMode');

    return (
        <>
            <div className={styles.formGroup}>
                <label className={styles.groupLabel}>SMS Type</label>
                <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                        <input
                            type='radio'
                            name='sms-mode'
                            value='static'
                            checked={smsMode === 'static'}
                            onChange={() => setValue('smsMode', 'static')}
                            className={styles.radio}
                        />
                        <span>Static</span>
                    </label>
                    <label className={styles.radioLabel}>
                        <input
                            type='radio'
                            name='sms-mode'
                            value='agentic'
                            checked={smsMode === 'agentic'}
                            onChange={() => setValue('smsMode', 'agentic')}
                            className={styles.radio}
                        />
                        <span>Agentic</span>
                    </label>
                </div>
            </div>

            {smsMode === 'static' ? (
                <TextArea id='message-content' label='Message Content' rows={4} {...register('messageContent')} />
            ) : (
                <>
                    {prompts && !prompts.length ? (
                        <div className={styles.noPromptsMessage}>No prompts available to select.</div>
                    ) : (
                        <Select
                            id='langfuse-prompt'
                            label='AI Prompt'
                            options={[
                                { value: '', label: '--Select Prompt--' },
                                ...(prompts?.map((prompt) => ({
                                    value: prompt.id,
                                    label: prompt.prompt_name,
                                })) || []),
                            ]}
                            {...register('langfusePromptId', {
                                validate: (v) => smsMode !== 'agentic' || !!v || 'Please select an AI prompt.',
                            })}
                            disabled={isLoadingPrompts}
                            error={!!errors.langfusePromptId}
                            errorText={errors.langfusePromptId?.message}
                        />
                    )}
                    <div className={styles.durationSection}>
                        <Input
                            id='user-abandoned-timeout'
                            label='User Timeout (hours)'
                            type='number'
                            min={1}
                            {...register('userAbandonedTimeoutHours', {
                                valueAsNumber: true,
                                validate: (v) => smsMode !== 'agentic' || v >= 1 || 'Must be at least 1 hour.',
                            })}
                            error={!!errors.userAbandonedTimeoutHours}
                            errorText={errors.userAbandonedTimeoutHours?.message}
                        />
                        <Input
                            id='agent-abandoned-timeout'
                            label='Agent Timeout (hours)'
                            type='number'
                            min={1}
                            {...register('agentAbandonedTimeoutHours', {
                                valueAsNumber: true,
                                validate: (v) => smsMode !== 'agentic' || v >= 1 || 'Must be at least 1 hour.',
                            })}
                            error={!!errors.agentAbandonedTimeoutHours}
                            errorText={errors.agentAbandonedTimeoutHours?.message}
                        />
                    </div>
                    <Controller
                        name='expectResponse'
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                id='expect-response'
                                label='Expect Response'
                                checked={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </>
            )}
        </>
    );
};
